// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { verifyToken, createOrUpdateUser, refreshToken } from '../services/authService';

interface User {
  privyUserId: string;
  walletAddress?: string;
  embeddedWalletAddress?: string;
  email?: string;
  balance: number;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (token: string, privyUserId: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  refreshAuthToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [lastTokenRefresh, setLastTokenRefresh] = useState(0);
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Helper function to get available tokens
  const getAuthToken = (): string | null => {
    return localStorage.getItem('auth_token') || localStorage.getItem('privy:token');
  };

  // Helper function to extract user ID from JWT or get from localStorage
  const getPrivyUserId = (): string | null => {
    // Try to get from localStorage directly
    const tokenValue = localStorage.getItem('privy:token');
    
    // Try direct extraction from auth_token
    const authToken = localStorage.getItem('auth_token');
    
    let storedUserId = null;
    
    if (tokenValue) {
      try {
        // Extract user ID from privy:token
        const tokenParts = tokenValue.split('.');
        if (tokenParts.length >= 2) {
          const payload = JSON.parse(atob(tokenParts[1]));
          storedUserId = payload.sub;
        }
      } catch (e) {
        console.error('Failed to parse privy:token payload:', e);
      }
    }
    
    // If not found, try to extract from auth_token
    if (!storedUserId && authToken) {
      try {
        if (authToken.split('.').length >= 2) {
          const payload = JSON.parse(atob(authToken.split('.')[1]));
          storedUserId = payload.user?.privyUserId || payload.sub;
        }
      } catch (e) {
        console.error('Failed to parse auth_token payload:', e);
      }
    }
    
    return storedUserId;
  };

  // Handle automatic token refresh
  useEffect(() => {
    // Calculate refresh interval based on token expiration
    // JWT tokens typically expire in 1 hour, so refresh every 50 minutes
    const refreshInterval = 50 * 60 * 1000; // 50 minutes
    
    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
    
    // Only set up interval if user is logged in
    if (user && user.privyUserId) {
      console.log('Setting up token refresh interval');
      refreshIntervalRef.current = setInterval(async () => {
        console.log('Auto-refreshing token...');
        try {
          await refreshAuthToken();
        } catch (error) {
          console.error('Error auto-refreshing token:', error);
        }
      }, refreshInterval);
    }
    
    // Clean up interval on unmount
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [user?.privyUserId]);

  useEffect(() => {
    const initAuth = async () => {
      const token = getAuthToken();
      const privyUserId = getPrivyUserId();
      
      if (token && privyUserId && token.startsWith('eyJ')) { // Basic check that it looks like a JWT
        try {
          await login(token, privyUserId);
        } catch (error) {
          console.error('Failed to restore authentication:', error);
          
          // If token is expired, try to refresh it
          if (error instanceof Error && 
              (error.message.includes('expired') || 
               error.message.includes('invalid token'))) {
            try {
              const newToken = await refreshAuthToken();
              if (newToken && privyUserId) {
                await login(newToken, privyUserId);
                return;
              }
            } catch (refreshError) {
              console.error('Failed to refresh token:', refreshError);
            }
          }
          
          // Don't show error on initial load
          logout();
        }
      } else {
        // Clear any invalid tokens
        if (token || privyUserId) {
          logout();
        } else {
          setIsLoading(false);
        }
      }
    };

    initAuth().catch(err => {
      console.error('Unhandled error during authentication initialization:', err);
      setIsLoading(false); // Ensure loading state is set to false even if there's an error
      logout();
    });
  }, []);

  const clearError = () => {
    setError(null);
  };

  const refreshAuthToken = async (): Promise<string | null> => {
    // Limit refresh attempts to prevent spamming the server
    const now = Date.now();
    if (now - lastTokenRefresh < 10000) { // 10 seconds minimum between refresh attempts
      console.log('Token refresh throttled, try again later');
      return null;
    }
    
    setLastTokenRefresh(now);
    const privyUserId = getPrivyUserId();
    
    if (!privyUserId) {
      return null;
    }
    
    try {
      console.log('Refreshing authentication token...');
      const response = await refreshToken(privyUserId);
      
      if (response && response.token) {
        console.log('Token refreshed successfully');
        localStorage.setItem('auth_token', response.token);
        return response.token;
      }
      
      return null;
    } catch (error) {
      console.error('Error refreshing token:', error);
      return null;
    }
  };

  const login = async (token: string, privyUserId: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Verify token with backend
      await verifyToken(token);
      
      // Create or update user
      const userData = await createOrUpdateUser(privyUserId);
      
      // Save auth info
      localStorage.setItem('auth_token', token);
      
      setUser(userData);
      setLoginAttempts(0); // Reset login attempts on success
    } catch (error: any) {
      console.error('Login failed:', error);
      
      // Handle rate limiting specifically
      if (error.message && error.message.includes('busy') || error.message.includes('rate limit')) {
        setLoginAttempts(prev => prev + 1);
        
        // For first few attempts, show a "trying again" message
        if (loginAttempts < 3) {
          setError(`The authentication service is busy. Please wait a moment and try again (Attempt ${loginAttempts + 1}/3).`);
        } else {
          // After multiple attempts, suggest waiting longer
          setError('The authentication service is experiencing high traffic. Please try again in a few minutes.');
        }
      } else {
        // For other errors, show the specific message
        setError(error.message || 'Authentication failed. Please try again.');
      }
      
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    // We don't remove privy:token as it's managed by the Privy library
    
    // Clear refresh interval on logout
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
    
    setUser(null);
    setIsLoading(false);
    setError(null);
    setLoginAttempts(0);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        error,
        login,
        logout,
        clearError,
        refreshAuthToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};